import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Thermometer,
  Zap,
  Server,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Download,
  Package,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  formatHashrate,
  formatPower,
  formatUptime,
  tempColor,
  timeAgo,
} from "@/lib/format";

function StatCard({ label, value, sub, color = "text-[#FAFAFA]", testid }) {
  return (
    <div className="bento p-5" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
        {label}
      </div>
      <div className={`font-mono text-3xl tracking-tighter mt-2 ${color}`}>
        {value}
      </div>
      {sub && (
        <div className="font-mono text-xs text-[#A1A1AA] mt-1">{sub}</div>
      )}
    </div>
  );
}

function RigCard({ rig }) {
  const online = rig.status === "online";
  const gpus = rig.gpus || [];
  const maxTemp = gpus.length
    ? Math.max(...gpus.map((g) => g.temperature || 0))
    : 0;
  const issues = gpus.filter((g) => (g.temperature || 0) >= 75);

  return (
    <Link
      to={`/rigs/${rig.id}`}
      data-testid={`rig-card-${rig.name}`}
      className="bento p-5 group hover:border-[#3F3F46] hover:-translate-y-0.5 transition-all duration-200 block"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                online
                  ? "bg-[#10B981] status-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                  : "bg-[#EF4444] shadow-[0_0_8px_rgba(239,68,68,0.5)]"
              }`}
            />
            <div className="font-heading font-bold text-lg truncate">
              {rig.name}
            </div>
          </div>
          <div className="font-mono text-[11px] text-[#A1A1AA] mt-1">
            {rig.ip || "—"} · {rig.gpu_count || gpus.length} GPU
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#A1A1AA] group-hover:text-[#10B981] transition-colors" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Hashrate
          </div>
          <div className="font-mono text-xl text-[#10B981] mt-0.5">
            {online ? formatHashrate(rig.total_hashrate) : "—"}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Power
          </div>
          <div className="font-mono text-xl text-[#F59E0B] mt-0.5">
            {online ? formatPower(rig.total_power) : "—"}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Max Temp
          </div>
          <div className={`font-mono text-xl mt-0.5 ${online ? tempColor(maxTemp) : "text-[#A1A1AA]"}`}>
            {online && maxTemp ? `${maxTemp.toFixed(0)}°C` : "—"}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Uptime
          </div>
          <div className="font-mono text-xl text-[#FAFAFA] mt-0.5">
            {online ? formatUptime(rig.miner_uptime_sec) : timeAgo(rig.last_seen)}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider">
        <div className="flex items-center gap-1.5">
          {online ? (
            rig.miner_running ? (
              <span className="text-[#10B981] flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> miner attivo
              </span>
            ) : (
              <span className="text-[#F59E0B] flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> miner fermo
              </span>
            )
          ) : (
            <span className="text-[#EF4444] flex items-center gap-1">
              <XCircle className="w-3 h-3" /> offline
            </span>
          )}
        </div>
        {issues.length > 0 && online && (
          <span className="text-[#F59E0B] flex items-center gap-1">
            <Thermometer className="w-3 h-3" /> {issues.length} HOT
          </span>
        )}
      </div>
    </Link>
  );
}

export default function DashboardPage() {
  const [rigs, setRigs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [latestVersions, setLatestVersions] = useState({});
  const [minerModalOpen, setMinerModalOpen] = useState(false);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([api.get("/rigs"), api.get("/stats/summary")]);
      setRigs(r.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  };

  const loadLatestVersions = async () => {
    try {
      const { data } = await api.get("/miners/latest-versions");
      setLatestVersions(data.versions || {});
    } catch (e) {
      // ignore: feature opzionale, fallback su empty
    }
  };

  useEffect(() => {
    load();
    loadLatestVersions();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const minersWithVersions = Object.values(latestVersions || {});
  const hasAnyVersion = minersWithVersions.length > 0;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10B981] mb-1.5">
            // Rig Fleet Overview
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Dashboard
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Monitoraggio live di tutti i mining rig.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="update-miners-btn"
            onClick={() => setMinerModalOpen(true)}
            disabled={!hasAnyVersion}
            title={hasAnyVersion ? "Aggiorna binari miner (bzminer/SRBMiner) sui rig" : "Caricamento versioni GitHub…"}
            className="relative flex items-center gap-2 px-3 py-2 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm text-xs font-mono uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Package className="w-3.5 h-3.5" />
            Update Miners
            {hasAnyVersion && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#F59E0B] text-[#09090B] rounded-full text-[9px] font-bold flex items-center justify-center">
                {minersWithVersions.length}
              </span>
            )}
          </button>
          <button
            data-testid="fleet-update-btn"
            onClick={async () => {
              if (!window.confirm("Push self-update a TUTTI i rig?\n\nOgni rig scarica il nuovo agent.py da francominer.com, valida la sintassi e si riavvia via systemd.")) return;
              try {
                const r = await api.post("/rigs/fleet/self-update");
                toast.success(`Update inviato a ${r.data.rigs_notified} rig`);
              } catch (e) {
                toast.error("Push fallito: " + (e.response?.data?.detail || e.message));
              }
            }}
            className="flex items-center gap-2 px-3 py-2 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/40 text-[#10B981] rounded-sm text-xs font-mono uppercase tracking-wider transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Update Fleet
          </button>
          <button
            data-testid="refresh-btn"
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 bg-[#18181B] border border-[#27272A] hover:border-[#3F3F46] rounded-sm text-xs font-mono uppercase tracking-wider text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8 stagger-fade">
        <StatCard
          label="Total Hashrate"
          value={summary ? formatHashrate(summary.total_hashrate) : "—"}
          color="text-[#10B981]"
          testid="stat-hashrate"
        />
        <StatCard
          label="Total Power"
          value={summary ? formatPower(summary.total_power) : "—"}
          color="text-[#F59E0B]"
          testid="stat-power"
        />
        <StatCard
          label="Rigs Online"
          value={summary ? `${summary.rigs_online}/${summary.rigs_total}` : "—"}
          color="text-[#FAFAFA]"
          testid="stat-online"
        />
        <StatCard
          label="Rigs Offline"
          value={summary?.rigs_offline ?? "—"}
          color={summary?.rigs_offline ? "text-[#EF4444]" : "text-[#A1A1AA]"}
          testid="stat-offline"
        />
        <StatCard
          label="Total GPUs"
          value={summary?.total_gpus ?? "—"}
          color="text-[#FAFAFA]"
          testid="stat-gpus"
        />
        <StatCard
          label="Avg / Max Temp"
          value={summary ? `${(summary.avg_temp || 0).toFixed(0)}° / ${(summary.max_temp || 0).toFixed(0)}°` : "—"}
          color="text-[#FAFAFA]"
          testid="stat-temp"
        />
      </div>

      {/* Rig grid */}
      {rigs.length === 0 ? (
        <div className="bento p-12 text-center" data-testid="empty-state">
          <Server className="w-10 h-10 text-[#A1A1AA] mx-auto mb-3" />
          <h3 className="font-heading font-bold text-xl mb-2">
            Nessun rig connesso
          </h3>
          <p className="text-sm text-[#A1A1AA] max-w-md mx-auto mb-5">
            Installa l'agent sui tuoi 15 PC Ubuntu Server per iniziare a
            monitorare hashrate, temperature e consumi.
          </p>
          <Link
            to="/agent"
            data-testid="empty-install-btn"
            className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#09090B] font-bold rounded-sm px-4 py-2 text-sm uppercase tracking-wider"
          >
            <Activity className="w-4 h-4" /> Vai a Agent Install
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 stagger-fade">
          {rigs.map((rig) => (
            <RigCard key={rig.id} rig={rig} />
          ))}
        </div>
      )}

      <UpdateMinersModal
        open={minerModalOpen}
        onClose={() => setMinerModalOpen(false)}
        versions={latestVersions}
        rigCount={rigs.length}
      />
    </div>
  );
}


function UpdateMinersModal({ open, onClose, versions, rigCount }) {
  const minerKeys = Object.keys(versions || {});
  const [miner, setMiner] = useState(minerKeys[0] || "");
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    if (minerKeys.length && !miner) setMiner(minerKeys[0]);
  }, [minerKeys, miner]);

  if (!open) return null;
  const selected = versions[miner] || {};

  const onPush = async () => {
    if (!selected.download_url) {
      toast.error("download_url mancante per " + miner);
      return;
    }
    if (!window.confirm(
      `Push aggiornamento ${miner} → v${selected.version} a TUTTI i ${rigCount} rig?\n\n` +
      `Ogni rig fermerà il miner, scaricherà il nuovo binario e ripartirà.\n` +
      `Tempo stimato: ~30-60s per rig.`,
    )) return;
    setPushing(true);
    try {
      const r = await api.post("/miners/fleet-update", {
        miner,
        version: selected.version,
        download_url: selected.download_url,
      });
      toast.success(`Update ${miner} v${selected.version} inviato a ${r.data.rigs_notified} rig`);
      onClose();
    } catch (e) {
      toast.error("Push fallito: " + (e.response?.data?.detail || e.message));
    } finally {
      setPushing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="update-miners-modal"
    >
      <div
        className="bento max-w-lg w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A1AA] hover:text-[#FAFAFA]"
          data-testid="update-miners-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F59E0B] mb-1 flex items-center gap-2">
          <Package className="w-3 h-3" />
          // Fleet Miner Update
        </div>
        <h2 className="font-heading font-black text-2xl mb-1">Update miner binari</h2>
        <p className="text-xs text-[#A1A1AA] mb-5">
          Versioni rilevate da GitHub releases. Scaricamento+install runs automatico
          su ogni rig dopo conferma.
        </p>

        <label className="block text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA] mb-1.5">
          Miner
        </label>
        <select
          value={miner}
          onChange={(e) => setMiner(e.target.value)}
          data-testid="update-miners-select"
          className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#F59E0B] mb-4"
        >
          {minerKeys.map((k) => (
            <option key={k} value={k}>
              {k} → v{versions[k].version}
            </option>
          ))}
        </select>

        {selected.version && (
          <div className="border border-[#27272A] rounded-sm p-3 mb-5 text-xs font-mono space-y-1">
            <div><span className="text-[#A1A1AA]">Tag:</span> <span className="text-[#10B981]">v{selected.version}</span></div>
            <div><span className="text-[#A1A1AA]">Asset:</span> <span className="text-[#FAFAFA] break-all">{selected.asset_name || "—"}</span></div>
            <div><span className="text-[#A1A1AA]">Pubblicato:</span> <span className="text-[#FAFAFA]">{selected.published_at?.slice(0, 10) || "—"}</span></div>
            {selected.html_url && (
              <div><a href={selected.html_url} target="_blank" rel="noreferrer" className="text-[#10B981] hover:underline">Vedi release su GitHub →</a></div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-xs font-mono uppercase tracking-wider text-[#A1A1AA] hover:text-[#FAFAFA] border border-[#27272A] rounded-sm"
          >
            Annulla
          </button>
          <button
            onClick={onPush}
            disabled={pushing || !selected.download_url}
            data-testid="update-miners-confirm"
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-[#09090B] bg-[#F59E0B] hover:bg-[#FBBF24] rounded-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pushing ? "Push…" : `Aggiorna ${rigCount} rig`}
          </button>
        </div>
      </div>
    </div>
  );
}
